import { DatabaseService } from "src/app/database/database.service";
import {IDBPDatabase, IDBPTransaction, openDB} from 'idb'
import TableConfig from "../model/tableConfig";
import IndexModel from "../model/indexModel";

export default class DatabaseTable<T> {

    //#region Propiedades

    public database: DatabaseService
    protected tableName: string
    protected primaryKeyField: string
    protected autoIncrement: boolean
    protected indexes: IndexModel[] = []
    private TablesChecked: boolean = false
    public getName(): string {
        return this.tableName
    }

    //#endregion

    //#region Constructor

    constructor(database: DatabaseService, config: TableConfig) {
        this.database = database
        this.tableName = config.tableName
        this.primaryKeyField = config.primaryKeyField || 'Id'
        this.autoIncrement = config.autoIncrement
        this.indexes = config.indexes
        if (this.autoIncrement == null) this.autoIncrement = true
        this.database.Tables.push(this)
    }

    //#endregion

    //#region Creación de la tabla

    protected async CheckDatabaseUpgrade(): Promise<void> {
        if (this.TablesChecked) return

        if (!('indexedDB' in window)) {
            throw new Error('Su navegador no es compatible con esta aplicación.')
        }

        await openDB(this.database.Name, this.database.Version, {
            upgrade: (db, oldVer, newVer, transaction) => {
                this.database.UpgradeDatabases(db, transaction)
            }
        })

        this.TablesChecked = true

    }

    public async UpgradeTable(db: IDBPDatabase, transaction: IDBPTransaction<unknown, string, 'versionchange'>): Promise<void> {
        if (!db.objectStoreNames.contains(this.tableName)) {
            const store = db.createObjectStore(this.tableName, {
                keyPath: this.primaryKeyField,
                autoIncrement: this.autoIncrement
            })
            this.indexes.forEach(p => {
                store.createIndex(p.Name, p.Field, {
                    unique: p.Unique
                })
            })
        } else {
            let store = transaction.objectStore(this.tableName)
            this.indexes.forEach(p => {
                if (!store.indexNames.contains(p.Name)) {
                    store.createIndex(p.Name, p.Field, {
                        unique: p.Unique
                    })
                }
            })
        }
    }

    //#endregion

    //#region Funciones

    protected async GetAllRecords(): Promise<T[]> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const allValues: T[] = await db.getAll(this.tableName)
        return allValues
    }

    protected async GetRecordsPage(start: number, limit: number): Promise<T[]> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = await db.transaction(this.tableName, 'readonly')
        let cursor = await tx.store.openCursor()
        if (!cursor) {
            return []
        }
        let data: T[] = []
        if (start > await tx.store.count()) {
            return []
        }
        if (start > 0) {
            await cursor.advance(start)
        }
        for (let i = 0; (i < limit) && cursor; ++i) {
            data.push(cursor.value as T)
            cursor = await cursor.continue()
        }
        return data
    }

    protected async GetRecordsCount(): Promise<number> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = await db.transaction(this.tableName, 'readonly')
        let count = await tx.store.count()
        return count
    }

    protected async GetRecordsCountIndex(indexName: string, lowerBound?: any, upperBound?: any): Promise<number> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = await db.transaction(this.tableName, 'readonly')

        let range: IDBKeyRange
        if (lowerBound != null && upperBound != null) {
            range = IDBKeyRange.bound(lowerBound, upperBound)
        } else if (lowerBound == null && upperBound != null) {
            range = IDBKeyRange.upperBound(upperBound)
        } else if (lowerBound != null && upperBound == null) {
            range = IDBKeyRange.lowerBound(lowerBound)
        }

        const index = tx.store.index(indexName)
        return await index.count(range)
    }

    // Devuelve las claves primarias de los registros cuyo campo indexado vale exactamente 'value' (p.ej. un índice booleano).
    // Pensado para poder combinar (intersecar en JS) varios filtros de igualdad sin necesidad de una consulta compuesta,
    // que IndexedDB no soporta de forma nativa.
    protected async GetKeysByIndex(indexName: string, value: any): Promise<any[]> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = await db.transaction(this.tableName, 'readonly')
        const index = tx.store.index(indexName)
        return await index.getAllKeys(IDBKeyRange.only(value))
    }

    // Recupera varios registros a la vez por su clave primaria (p.ej. el resultado de intersecar varios GetKeysByIndex).
    protected async GetRecordsByKeys(keys: any[]): Promise<T[]> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = db.transaction(this.tableName, 'readonly')
        const registros = await Promise.all(keys.map(key => tx.store.get(key)))
        await tx.done
        return registros.filter(p => p != null) as T[]
    }

    protected async GetRecordsById(start: number, limit: number, lowerBound?: any, upperBound?: any): Promise<T[]> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = await db.transaction(this.tableName, 'readonly')
        let range: IDBKeyRange
        if (lowerBound != null && upperBound != null) {
            range = IDBKeyRange.bound(lowerBound, upperBound)
        } else if (lowerBound == null && upperBound != null) {
            range = IDBKeyRange.upperBound(upperBound)
        } else if (lowerBound != null && upperBound == null) {
            range = IDBKeyRange.lowerBound(lowerBound)
        }

        const index = tx.store
        let cursor = await index.openCursor(range)

        if (!cursor) {
            return []
        }

        let data: T[] = []
        if (start > await index.count(range)) {
            return []
        }
        if (start > 0) {
            await cursor.advance(start)
        }
        for (let i = 0; (i < limit) && cursor; ++i) {
            data.push(cursor.value as T)
            cursor = await cursor.continue()
        }
        return data
    }

    protected async GetRecordsByIndex(start: number, limit: number, indexName: string, lowerBound?: any, upperBound?: any): Promise<T[]> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = await db.transaction(this.tableName, 'readonly')
        let range: IDBKeyRange
        if (lowerBound != null && upperBound != null) {
            range = IDBKeyRange.bound(lowerBound, upperBound)
        } else if (lowerBound == null && upperBound != null) {
            range = IDBKeyRange.upperBound(upperBound)
        } else if (lowerBound != null && upperBound == null) {
            range = IDBKeyRange.lowerBound(lowerBound)
        }

        const index = tx.store.index(indexName)
        let cursor = await index.openCursor(range)

        if (!cursor) {
            return []
        }

        let data: T[] = []
        if (start > await index.count(range)) {
            return []
        }
        if (start > 0) {
            await cursor.advance(start)
        }
        for (let i = 0; (i < limit) && cursor; ++i) {
            data.push(cursor.value as T)
            cursor = await cursor.continue()
        }
        return data
    }

    protected async UpdateRecord(record: T): Promise<any> {
        await this.CheckDatabaseUpgrade();
        const db = await openDB(this.database.Name, this.database.Version);
        const tx = await db.transaction(this.tableName, 'readwrite');
    
        // Guardar el registro y capturar el ID generado
        const generatedId = await tx.store.put(record);
        await tx.done;
    
        // Retornar el ID generado
        return generatedId;
    }

    protected async DeleteRecord(id: any): Promise<void> {
        await this.CheckDatabaseUpgrade()
        const db = await openDB(this.database.Name, this.database.Version)
        const tx = await db.transaction(this.tableName, 'readwrite')
        await Promise.all([
            tx.store.delete(id),
            tx.done
        ])
    }

    //#endregion

}